import {
  ACTIVE_REVIEW_STATUSES,
  ALL_REVIEW_SCOPES,
  ConceptVerdictItem,
  ContractParametrizationState,
  ContractReview,
  ContractReviewFinding,
  ContractReviewScope,
  ContractReviewStatus,
  DECIDABLE_REVIEW_STATUSES,
  FINDING_SEVERITIES,
  FindingApplyStatus,
  FindingDecision,
  FindingDecisionItem,
  FindingSeverity,
  MAX_BULK_DECISIONS,
  NewAddendumFields,
  SYSTEM_DECIDER,
  UNDOABLE_REVIEW_STATUSES
} from '../../../models/Entities/Contracts/ContractReview';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import { ContractAddendumType } from '../../../models/Entities/Contracts/NewContractAddendum';
import { ContractParametrizationEntry } from '../../../redux/states/ContractReview.States';
import { isCurrentAddendum } from '../Addendums/Addendums.List/Addendums.Functions';

// Revisión de contratos con IA (spec 012): funciones puras de normalización, filtros, agrupación y resumen.

const KNOWN_STATUSES: ContractReviewStatus[] = ['Queued', 'Running', 'Completed', 'Failed', 'Applied', 'PartiallyApplied', 'Reverted'];

const asNumber = (value: unknown, fallback = 0): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const asStringOrNull = (value: unknown): string | null => (value === undefined || value === null ? null : String(value));

/** Un hallazgo tal como llegue (texto, números como texto, códigos desconocidos), con los valores por defecto del contrato. */
export const normalizeFinding = (raw: any): ContractReviewFinding => ({
  id: asNumber(raw?.id),
  reviewId: String(raw?.reviewId ?? ''),
  ordinal: asNumber(raw?.ordinal),
  key: String(raw?.key ?? ''),
  scope: raw?.scope ?? 'Differences',
  category: String(raw?.category ?? ''),
  severity: FINDING_SEVERITIES.includes(raw?.severity) ? raw.severity : 'Info',
  title: String(raw?.title ?? ''),
  description: asStringOrNull(raw?.description),
  evidence: asArray<any>(raw?.evidence).map(e => ({
    page: e?.page === undefined || e?.page === null ? null : asNumber(e.page),
    fileName: asStringOrNull(e?.fileName),
    quote: String(e?.quote ?? '')
  })),
  target: raw?.target
    ? {
        entityType: raw.target.entityType,
        entityId: raw.target.entityId === undefined || raw.target.entityId === null ? null : asNumber(raw.target.entityId),
        label: String(raw.target.label ?? '')
      }
    : null,
  currentValue: asStringOrNull(raw?.currentValue),
  suggestedValue: asStringOrNull(raw?.suggestedValue),
  patch: raw?.patch
    ? {
        op: raw.patch.op,
        entityType: raw.patch.entityType,
        entityId: raw.patch.entityId === undefined || raw.patch.entityId === null ? null : asNumber(raw.patch.entityId),
        fields: raw.patch.fields && typeof raw.patch.fields === 'object' ? raw.patch.fields : {},
        appliesTo: raw.patch.appliesTo ?? null
      }
    : null,
  patchValid: !!raw?.patchValid,
  patchInvalidReason: asStringOrNull(raw?.patchInvalidReason),
  baseline: raw?.baseline ?? null,
  confidence: raw?.confidence === undefined || raw?.confidence === null ? null : asNumber(raw.confidence),
  origin: raw?.origin ?? null,
  decision: ['Pending', 'Accepted', 'Rejected'].includes(raw?.decision) ? raw.decision : 'Pending',
  decisionComment: asStringOrNull(raw?.decisionComment),
  decidedBy: asStringOrNull(raw?.decidedBy),
  decidedAt: asStringOrNull(raw?.decidedAt),
  overrideFields: raw?.overrideFields ?? null,
  applyStatus: raw?.applyStatus ?? null,
  applyMessage: asStringOrNull(raw?.applyMessage),
  appliedBy: asStringOrNull(raw?.appliedBy),
  appliedAt: asStringOrNull(raw?.appliedAt),
  currentValues: raw?.currentValues ?? null,
  previousValues: raw?.previousValues ?? null,
  appliedValues: raw?.appliedValues ?? null,
  createdEntityId: raw?.createdEntityId === undefined || raw?.createdEntityId === null ? null : asNumber(raw.createdEntityId),
  // Contracts 021 §3.2
  autoDecided: !!raw?.autoDecided,
  undoneAt: asStringOrNull(raw?.undoneAt),
  undoneBy: asStringOrNull(raw?.undoneBy),
  undoMessage: asStringOrNull(raw?.undoMessage),
  undoneValues: raw?.undoneValues ?? null,
  patchRef: asStringOrNull(raw?.patchRef)
});

export const normalizeVerdict = (raw: any): ConceptVerdictItem => ({
  contractConceptId: raw?.contractConceptId === undefined || raw?.contractConceptId === null ? null : asNumber(raw.contractConceptId),
  label: String(raw?.label ?? ''),
  verdict: raw?.verdict ?? 'Observations',
  checks: asArray<any>(raw?.checks).map(c => ({
    check: String(c?.check ?? ''),
    status: c?.status ?? 'NotApplicable',
    detail: asStringOrNull(c?.detail),
    origin: c?.origin ?? null
  })),
  findingKeys: asArray<string>(raw?.findingKeys).map(String),
  findingIds: asArray<unknown>(raw?.findingIds).map(id => asNumber(id))
});

/** Una revisión (resumen o detalle) con valores por defecto; un estado desconocido queda `Unknown`. */
export const normalizeReview = (raw: any): ContractReview => {
  const review: ContractReview = {
    id: String(raw?.id ?? ''),
    contractId: asNumber(raw?.contractId),
    mode: raw?.mode === 'NewAddendum' ? 'NewAddendum' : 'Review',
    addendumId: raw?.addendumId === undefined || raw?.addendumId === null ? null : asNumber(raw.addendumId),
    baseAddendumId: raw?.baseAddendumId === undefined || raw?.baseAddendumId === null ? null : asNumber(raw.baseAddendumId),
    createdAddendumId: raw?.createdAddendumId === undefined || raw?.createdAddendumId === null ? null : asNumber(raw.createdAddendumId),
    baseAddendumIsBlocked: raw?.baseAddendumIsBlocked === undefined ? null : raw.baseAddendumIsBlocked,
    status: KNOWN_STATUSES.includes(raw?.status) ? raw.status : 'Unknown',
    trigger: raw?.trigger === 'AfterImport' ? 'AfterImport' : 'Manual',
    importProcessId: asStringOrNull(raw?.importProcessId),
    stage: asStringOrNull(raw?.stage),
    progressPercent: Math.max(0, Math.min(100, asNumber(raw?.progressPercent))),
    model: asStringOrNull(raw?.model),
    scopes: asArray<ContractReviewScope>(raw?.scopes),
    fileIds: asArray<string>(raw?.fileIds).map(String),
    errorMessage: asStringOrNull(raw?.errorMessage),
    createdBy: String(raw?.createdBy ?? ''),
    created: String(raw?.created ?? ''),
    startTimestamp: asStringOrNull(raw?.startTimestamp),
    endTimestamp: asStringOrNull(raw?.endTimestamp),
    appliedBy: asStringOrNull(raw?.appliedBy),
    appliedAt: asStringOrNull(raw?.appliedAt),
    findingCount: asNumber(raw?.findingCount),
    pendingCount: asNumber(raw?.pendingCount),
    acceptedCount: asNumber(raw?.acceptedCount),
    rejectedCount: asNumber(raw?.rejectedCount),
    appliedCount: asNumber(raw?.appliedCount),
    conflictCount: asNumber(raw?.conflictCount),
    skippedCount: asNumber(raw?.skippedCount),
    failedCount: asNumber(raw?.failedCount),
    // Contracts 021 §3.2
    autoApply: !!raw?.autoApply,
    undoneCount: asNumber(raw?.undoneCount)
  };

  // Los campos del detalle sólo si vinieron: así el polling de la lista no borra los hallazgos (mergeReview).
  if (raw && 'findings' in raw) {
    review.findings = asArray<any>(raw.findings)
      .map(normalizeFinding)
      .sort((a, b) => a.ordinal - b.ordinal || a.id - b.id);
  }
  if (raw && 'conceptVerdicts' in raw) review.conceptVerdicts = asArray<any>(raw.conceptVerdicts).map(normalizeVerdict);
  if (raw && 'warnings' in raw) review.warnings = asArray<string>(raw.warnings).map(String);
  if (raw && 'extractionSummary' in raw) review.extractionSummary = raw.extractionSummary ?? null;
  if (raw && 'instructions' in raw) review.instructions = asStringOrNull(raw.instructions);
  return review;
};

// ── Estado ───────────────────────────────────────────────────────────────────────────

export const isReviewActive = (review: Pick<ContractReview, 'status'> | undefined): boolean =>
  !!review && ACTIVE_REVIEW_STATUSES.includes(review.status);

/** Una adenda se revisa con IA si es de tipo contrato (nunca una instancia de negociación, DEC-007). */
export const isReviewableAddendum = (addendum: Pick<ContractAddendum, 'contractAddendumType'> | undefined): boolean =>
  !!addendum && addendum.contractAddendumType !== ContractAddendumType.NegociationInstance;

/**
 * Se puede decidir con la revisión terminada (Completed, Applied o PartiallyApplied) y el hallazgo sin aplicar;
 * también con `Conflict`, `Skipped` o `Failed` (DEC-005).
 */
export const canDecide = (review: Pick<ContractReview, 'status'> | undefined, finding: ContractReviewFinding): boolean =>
  !!review && DECIDABLE_REVIEW_STATUSES.includes(review.status) && finding.applyStatus !== 'Applied';

/** Aceptados con cambio válido que todavía no se aplicaron (o fallaron): los que procesaría "Aplicar". */
export const getApplicableFindings = (review: ContractReview | undefined): ContractReviewFinding[] =>
  (review?.findings ?? []).filter(
    f =>
      f.decision === 'Accepted' &&
      !!f.patch &&
      f.patchValid &&
      (f.applyStatus === null || f.applyStatus === 'Failed' || f.applyStatus === 'Undone')
  );

// ── Deshacer y aplicación automática (spec 015; Contracts 021 §3.2 y §4) ──────────────

/** La decisión la tomó el sistema al aplicar solo (`decidedBy = "IA"`). */
export const isAutoDecided = (finding: Pick<ContractReviewFinding, 'autoDecided' | 'decidedBy'>): boolean =>
  !!finding.autoDecided || finding.decidedBy === SYSTEM_DECIDER;

/**
 * Se deshace un hallazgo aplicado de una revisión terminada. El alta de la adenda de una revisión `NewAddendum` no:
 * se deshace con la revisión completa (Contracts 021 §4.1).
 */
export const canUndoFinding = (review: Pick<ContractReview, 'status' | 'mode'> | undefined, finding: ContractReviewFinding): boolean =>
  !!review &&
  UNDOABLE_REVIEW_STATUSES.includes(review.status) &&
  finding.applyStatus === 'Applied' &&
  !(review.mode === 'NewAddendum' && isAddendumCreate(finding));

/** Los hallazgos que procesaría "Deshacer revisión". */
export const getUndoableFindings = (review: ContractReview | undefined): ContractReviewFinding[] =>
  (review?.findings ?? []).filter(f => f.applyStatus === 'Applied');

/** La revisión se deshace entera mientras le quede algún hallazgo aplicado (Contracts 021 §4.2). */
export const canUndoReview = (review: ContractReview | undefined): boolean =>
  !!review &&
  UNDOABLE_REVIEW_STATUSES.includes(review.status) &&
  (review.findings ? getUndoableFindings(review).length > 0 : review.appliedCount > 0);

// ── Filtros, agrupación y resumen ────────────────────────────────────────────────────

export interface FindingFilters {
  scopes: ContractReviewScope[];
  categories: string[];
  severities: FindingSeverity[];
  decisions: FindingDecision[];
  applyStatuses: (FindingApplyStatus | 'NotApplied')[];
  onlyWithPatch: boolean;
  contractConceptId?: number;
  findingIds?: number[];
  text: string;
  /** Estado de trabajo (rediseño): qué pestaña de la lista se ve. Sin valor, la página elige. */
  state?: WorkStateFilter;
}

export const EMPTY_FILTERS: FindingFilters = {
  scopes: [],
  categories: [],
  severities: [],
  decisions: [],
  applyStatuses: [],
  onlyWithPatch: false,
  contractConceptId: undefined,
  findingIds: undefined,
  text: '',
  state: undefined
};

export const hasActiveFilters = (filters: FindingFilters): boolean =>
  filters.scopes.length > 0 ||
  filters.categories.length > 0 ||
  filters.severities.length > 0 ||
  filters.decisions.length > 0 ||
  filters.applyStatuses.length > 0 ||
  filters.onlyWithPatch ||
  filters.contractConceptId !== undefined ||
  (filters.findingIds?.length ?? 0) > 0 ||
  filters.text.trim().length > 0;

const matchesText = (finding: ContractReviewFinding, text: string): boolean => {
  const needle = text.trim().toLocaleLowerCase();
  if (!needle) return true;
  return [finding.title, finding.description, finding.target?.label, finding.category, ...finding.evidence.map(e => e.quote)]
    .filter(Boolean)
    .some(value => String(value).toLocaleLowerCase().includes(needle));
};

export const filterFindings = (findings: ContractReviewFinding[], filters: FindingFilters): ContractReviewFinding[] =>
  findings.filter(
    f =>
      (filters.scopes.length === 0 || filters.scopes.includes(f.scope)) &&
      (filters.categories.length === 0 || filters.categories.includes(String(f.category))) &&
      (filters.severities.length === 0 || filters.severities.includes(f.severity)) &&
      (filters.decisions.length === 0 || filters.decisions.includes(f.decision)) &&
      (filters.applyStatuses.length === 0 || filters.applyStatuses.includes(f.applyStatus ?? 'NotApplied')) &&
      (!filters.onlyWithPatch || !!f.patch) &&
      (filters.findingIds === undefined || filters.findingIds.includes(f.id)) &&
      (filters.state === undefined || filters.state === 'all' || workStateOf(f) === filters.state) &&
      matchesText(f, filters.text)
  );

/** 0 = más grave. */
export const severityRank = (severity: FindingSeverity): number => {
  const index = FINDING_SEVERITIES.indexOf(severity);
  return index < 0 ? FINDING_SEVERITIES.length : index;
};

export interface FindingGroup {
  scope: ContractReviewScope;
  categories: { category: string; findings: ContractReviewFinding[] }[];
  count: number;
}

/** Alcance (en el orden del contrato) → categoría (la más grave primero) → hallazgos por gravedad y orden. */
export const groupFindings = (findings: ContractReviewFinding[]): FindingGroup[] => {
  const scopes = [...ALL_REVIEW_SCOPES, ...Array.from(new Set(findings.map(f => f.scope))).filter(s => !ALL_REVIEW_SCOPES.includes(s))];
  return scopes
    .map(scope => {
      const inScope = findings.filter(f => f.scope === scope);
      const byCategory = new Map<string, ContractReviewFinding[]>();
      inScope.forEach(f => byCategory.set(String(f.category), [...(byCategory.get(String(f.category)) ?? []), f]));
      const categories = Array.from(byCategory.entries())
        .map(([category, items]) => ({
          category,
          findings: [...items].sort((a, b) => severityRank(a.severity) - severityRank(b.severity) || a.ordinal - b.ordinal)
        }))
        .sort(
          (a, b) =>
            Math.min(...a.findings.map(f => severityRank(f.severity))) - Math.min(...b.findings.map(f => severityRank(f.severity))) ||
            a.category.localeCompare(b.category)
        );
      return { scope, categories, count: inScope.length };
    })
    .filter(group => group.count > 0);
};

export interface ReviewSummary {
  byScope: Record<ContractReviewScope, number>;
  bySeverity: Record<FindingSeverity, number>;
  byDecision: Record<FindingDecision, number>;
  byApplyStatus: Partial<Record<FindingApplyStatus, number>>;
  applicableCount: number;
  acceptedWithoutPatch: number;
}

/** El resumen se calcula con los hallazgos del detalle: la lista no trae gravedades. */
export const summarizeReview = (review: ContractReview | undefined): ReviewSummary => {
  const findings = review?.findings ?? [];
  const summary: ReviewSummary = {
    byScope: { Differences: 0, BestPractices: 0, Risks: 0, ConceptReview: 0 },
    bySeverity: { High: 0, Medium: 0, Low: 0, Info: 0 },
    byDecision: { Pending: 0, Accepted: 0, Rejected: 0 },
    byApplyStatus: {},
    applicableCount: getApplicableFindings(review).length,
    acceptedWithoutPatch: findings.filter(f => f.decision === 'Accepted' && (!f.patch || !f.patchValid)).length
  };
  findings.forEach(f => {
    summary.byScope[f.scope] = (summary.byScope[f.scope] ?? 0) + 1;
    summary.bySeverity[f.severity] = (summary.bySeverity[f.severity] ?? 0) + 1;
    summary.byDecision[f.decision] = (summary.byDecision[f.decision] ?? 0) + 1;
    if (f.applyStatus) summary.byApplyStatus[f.applyStatus] = (summary.byApplyStatus[f.applyStatus] ?? 0) + 1;
  });
  return summary;
};

/** Hasta 200 por pedido (DEC-004). */
export const chunkDecisions = <T,>(items: T[], size: number = MAX_BULK_DECISIONS): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

export const buildDecisionItems = (findingIds: number[], decision: FindingDecision, comment?: string | null): FindingDecisionItem[] =>
  findingIds.map(findingId => ({ findingId, decision, comment: comment?.trim() ? comment.trim() : null }));

/** `currentValue`/`suggestedValue`: JSON formateado si lo es, el texto si no, y "—" si no hay valor. */
export const formatJsonValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null ? JSON.stringify(parsed, null, 2) : String(parsed);
  } catch {
    return value;
  }
};

/** Un valor de un campo del `patch` para mostrar. */
/** Objetos y listas del parche con sangría: un JSON de una sola línea sin espacios no se puede cortar y ensancha la tabla. */
export const formatFieldValuePretty = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

export const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// ── URL (filtros ↔ query string) ─────────────────────────────────────────────────────

const LIST_KEYS: [keyof FindingFilters, string][] = [
  ['scopes', 'scope'],
  ['categories', 'category'],
  ['severities', 'severity'],
  ['decisions', 'decision'],
  ['applyStatuses', 'result']
];

export const filtersToQuery = (filters: FindingFilters, base?: URLSearchParams): URLSearchParams => {
  const params = new URLSearchParams(base);
  LIST_KEYS.forEach(([, key]) => params.delete(key));
  ['patch', 'concept', 'q', 'findings', 'state'].forEach(key => params.delete(key));
  LIST_KEYS.forEach(([field, key]) => {
    const values = filters[field] as string[];
    if (values.length > 0) params.set(key, values.join(','));
  });
  if (filters.onlyWithPatch) params.set('patch', '1');
  if (filters.contractConceptId !== undefined) params.set('concept', String(filters.contractConceptId));
  if (filters.findingIds && filters.findingIds.length > 0) params.set('findings', filters.findingIds.join(','));
  if (filters.text.trim()) params.set('q', filters.text.trim());
  if (filters.state) params.set('state', filters.state);
  return params;
};

export const queryToFilters = (params: URLSearchParams): FindingFilters => {
  const list = (key: string) => (params.get(key) ?? '').split(',').map(v => v.trim()).filter(Boolean);
  const concept = params.get('concept');
  const findings = list('findings').map(Number).filter(Number.isFinite);
  const state = params.get('state');
  return {
    scopes: list('scope') as ContractReviewScope[],
    categories: list('category'),
    severities: list('severity') as FindingSeverity[],
    decisions: list('decision') as FindingDecision[],
    applyStatuses: list('result') as (FindingApplyStatus | 'NotApplied')[],
    onlyWithPatch: params.get('patch') === '1',
    contractConceptId: concept !== null && concept !== '' && Number.isFinite(Number(concept)) ? Number(concept) : undefined,
    findingIds: findings.length > 0 ? findings : undefined,
    text: params.get('q') ?? '',
    state: isWorkStateFilter(state) ? state : undefined
  };
};

// ── Conceptos ────────────────────────────────────────────────────────────────────────

/** Los hallazgos de un veredicto, por `findingIds` (los de Contracts, resueltos desde `findingKeys`). */
export const findingsForConcept = (review: ContractReview | undefined, verdict: ConceptVerdictItem): ContractReviewFinding[] => {
  const ids = new Set(verdict.findingIds);
  return (review?.findings ?? []).filter(f => ids.has(f.id));
};

// ── D-6: parametrización ─────────────────────────────────────────────────────────────

/** `unknown` sin dato, cargando o con error; si no, según `isParameterized` (DEC-003). */
export const getParametrizationState = (entry: ContractParametrizationEntry | undefined): ContractParametrizationState => {
  if (!entry || entry.isLoading || entry.error || entry.isParameterized === undefined) return 'unknown';
  return entry.isParameterized ? 'parameterized' : 'empty';
};

export type ImportAvailability = 'unknown' | 'available' | 'blocked';

/**
 * Contracts 021 §2: se importa sin parametrización, o se reimporta sobre una adenda nueva mientras no haya
 * liquidaciones ni adendas sin vigencia. Si no, `importBlockedReason` dice por qué.
 */
export const getImportAvailability = (entry: ContractParametrizationEntry | undefined): ImportAvailability => {
  if (getParametrizationState(entry) === 'unknown') return 'unknown';
  return entry?.canImport === false ? 'blocked' : 'available';
};

// ── D-7: nueva adenda ────────────────────────────────────────────────────────────────

/** La vigente si la hay; si no, la primera de tipo contrato. Nunca una instancia de negociación (DEC-007). */
export const getDefaultBaseAddendum = (addendums: ContractAddendum[] | undefined): ContractAddendum | undefined => {
  const candidates = (addendums ?? []).filter(isReviewableAddendum);
  return candidates.find(a => isCurrentAddendum(a)) ?? candidates[0];
};

export interface NewAddendumView {
  finding: ContractReviewFinding | undefined;
  fields: NewAddendumFields | undefined;
  baseValidityFinding: ContractReviewFinding | undefined;
  changes: ContractReviewFinding[];
  canCreate: boolean;
}

export const isAddendumCreate = (f: ContractReviewFinding): boolean =>
  f.category === 'NewAddendum' && f.patch?.op === 'Create' && f.patch?.entityType === 'Addendum';

/**
 * Separa una revisión `NewAddendum` en el alta (primera), el ajuste opcional del fin de la base
 * (`ValidityMismatch` con `appliesTo = Base`) y el resto de los cambios. Rechazar el alta no deshabilita las demás
 * decisiones (DEC-006): sólo impide crear.
 */
export const splitNewAddendumReview = (review: ContractReview | undefined): NewAddendumView => {
  const findings = review?.findings ?? [];
  const finding = findings.find(isAddendumCreate);
  const baseValidityFinding = findings.find(
    f => f.category === 'ValidityMismatch' && f.patch?.appliesTo === 'Base' && f.patch?.entityType === 'Addendum'
  );
  return {
    finding,
    fields: finding?.patch ? (finding.patch.fields as unknown as NewAddendumFields) : undefined,
    baseValidityFinding,
    changes: findings.filter(f => f !== finding && f !== baseValidityFinding),
    canCreate: !!finding && finding.decision === 'Accepted' && finding.patchValid && finding.applyStatus !== 'Applied'
  };
};

/** El texto de error a mostrar: Contracts pone el mensaje exacto en `errors[0]` en los 400 de validación. */
export const errorText = (error: { ErrorMessage?: string; Errors?: string[] } | undefined): string | undefined =>
  error ? error.Errors?.find(e => !!e?.trim()) ?? error.ErrorMessage : undefined;

/** Fecha y hora local de un timestamp de la API; '—' si no hay. */
export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

// ── Lectura humana (rediseño de la página) ───────────────────────────────────────────

/**
 * Qué clase de ítem es para quien revisa:
 * - `change`: trae un parche válido, "Aplicar" lo escribe en el contrato;
 * - `manual`: habla de datos del contrato (diferencias, revisión de conceptos, un parche que no se puede aplicar o un
 *   valor sugerido) pero la IA no lo puede cargar sola: queda para cargarlo a mano;
 * - `observation`: una buena práctica o un riesgo, no cambia nada.
 */
export type FindingKind = 'change' | 'manual' | 'observation';

type KindSource = Pick<ContractReviewFinding, 'patch' | 'patchValid' | 'scope' | 'suggestedValue'>;

const DATA_SCOPES: ContractReviewScope[] = ['Differences', 'ConceptReview'];

export const findingKind = (finding: KindSource): FindingKind => {
  if (finding.patch && finding.patchValid) return 'change';
  if (finding.patch || DATA_SCOPES.includes(finding.scope) || !!finding.suggestedValue) return 'manual';
  return 'observation';
};

/** Todo lo que no es un aviso es un cambio (se aplique solo o a mano). */
export const isChangeFinding = (finding: KindSource): boolean => findingKind(finding) !== 'observation';

/** Referencia a un alta de la misma revisión, `{ ref: "stage:interim sc check ins@126" }` → la etiqueta legible. */
export const describeRef = (value: unknown): { entity: string; label: string } | undefined => {
  const ref = value && typeof value === 'object' ? (value as { ref?: unknown }).ref : undefined;
  if (typeof ref !== 'string') return undefined;
  const match = /^(?<entity>[\w-]+):(?<label>.*?)(?:@\d+)?$/.exec(ref);
  return match?.groups ? { entity: match.groups.entity, label: match.groups.label.trim() } : { entity: '', label: ref };
};

/** El objeto JSON de `suggestedValue` / `currentValue`; nada si no es un objeto. */
export const parseValueObject = (value: string | null | undefined): Record<string, unknown> | undefined => {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** Una cita que es una fila de tabla del PDF ("| a | | b | 9.00 |") se lee mejor con sus celdas no vacías separadas por " · ". */
export const cleanQuote = (quote: string): { text: string; table: boolean } => {
  const trimmed = quote.trim();
  const bars = (trimmed.match(/\|/g) ?? []).length;
  if (bars < 3) return { text: trimmed, table: false };
  const cells = trimmed
    .split('|')
    .map(cell => cell.trim())
    .filter(Boolean);
  return { text: cells.join(' · '), table: true };
};

export const truncateText = (text: string, max = 160): string => (text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`);

/** Un nombre de archivo largo conserva el principio y el final. */
export const shortFileName = (name: string, max = 44): string => {
  if (name.length <= max) return name;
  const keep = Math.floor((max - 1) / 2);
  return `${name.slice(0, keep)}…${name.slice(-keep)}`;
};

/** Saca de un texto del servicio las referencias técnicas entre paréntesis, como "(stage:interim sc check ins@126)". */
export const stripTechnicalRefs = (text: string): string => text.replace(/\s*\(\w+:[^()]*@\d+\)/g, '').replace(/\s{2,}/g, ' ').trim();

export type ReviewView = 'changes' | 'observations' | 'all';

export const filterByView = (findings: ContractReviewFinding[], view: ReviewView): ContractReviewFinding[] =>
  view === 'all' ? findings : findings.filter(f => (view === 'changes' ? isChangeFinding(f) : !isChangeFinding(f)));

/**
 * Estado de trabajo de un hallazgo, como lo ve quien revisa: por decidir, aceptado (todavía sin aplicar), rechazado o
 * aplicado. Son excluyentes: un aplicado no vuelve a "aceptados" aunque su decisión sea Accepted.
 */
export type WorkState = 'pending' | 'accepted' | 'rejected' | 'applied';
export const WORK_STATES: WorkState[] = ['pending', 'accepted', 'rejected', 'applied'];
export type WorkStateFilter = WorkState | 'all';

export const isWorkStateFilter = (value: unknown): value is WorkStateFilter =>
  value === 'all' || (WORK_STATES as string[]).includes(String(value));

export const workStateOf = (finding: Pick<ContractReviewFinding, 'decision' | 'applyStatus'>): WorkState => {
  if (finding.applyStatus === 'Applied') return 'applied';
  if (finding.decision === 'Accepted') return 'accepted';
  if (finding.decision === 'Rejected') return 'rejected';
  return 'pending';
};

export type WorkStateCounts = Record<WorkStateFilter, number>;

export const countByWorkState = (findings: ContractReviewFinding[]): WorkStateCounts => {
  const counts: WorkStateCounts = { pending: 0, accepted: 0, rejected: 0, applied: 0, all: findings.length };
  findings.forEach(f => {
    counts[workStateOf(f)] += 1;
  });
  return counts;
};

/** Estado inicial de la lista: lo que hay que decidir si queda algo; si no, todo. */
export const defaultWorkState = (findings: ContractReviewFinding[]): WorkStateFilter => (findings.some(f => workStateOf(f) === 'pending') ? 'pending' : 'all');

/** Orden de trabajo: lo más importante primero y, a igual importancia, el orden en que lo encontró la IA. */
export const sortForWork = (findings: ContractReviewFinding[]): ContractReviewFinding[] =>
  [...findings].sort((a, b) => severityRank(a.severity) - severityRank(b.severity) || a.ordinal - b.ordinal || a.id - b.id);

/** Aplicado pero el dato cambió o la base lo rechazó: hay algo que contar antes de volver a decidir. */
export const hasApplyProblem = (finding: Pick<ContractReviewFinding, 'applyStatus'>): boolean =>
  finding.applyStatus === 'Conflict' || finding.applyStatus === 'Skipped' || finding.applyStatus === 'Failed';

/** "PriceValue" → "Price Value" cuando no hay traducción del campo. */
export const humanizeField = (field: string): string => field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim();

export interface ChangeLine {
  field: string;
  current: string | undefined;
  next: string;
  /** El valor nuevo apunta a un alta de la misma revisión que todavía no existe en el sistema. */
  ref?: { entity: string; label: string };
  /** Los valores sin formatear, para mostrarlos en palabras (Sí/No, "Mensual", fecha local). */
  rawCurrent?: unknown;
  rawNext?: unknown;
}

const changeLines = (next: Record<string, unknown>, current: Record<string, unknown>): ChangeLine[] =>
  Object.entries(next).map(([field, value]) => ({
    field,
    current: field in current ? formatFieldValue(current[field]) : undefined,
    next: formatFieldValue(value),
    ref: describeRef(value),
    rawCurrent: field in current ? current[field] : undefined,
    rawNext: value
  }));

/**
 * Las líneas "campo: actual → nuevo" de un parche. El valor actual sale de `currentValues` (lo que Contracts
 * leyó al revisar) o de `baseline`; si no hay, sólo se muestra el nuevo.
 */
export const describeChange = (finding: ContractReviewFinding): ChangeLine[] => {
  if (!finding.patch) return [];
  return changeLines(finding.patch.fields, finding.currentValues ?? finding.baseline ?? {});
};

/** Para un cambio sin parche: las mismas líneas a partir del valor sugerido (y el actual, si vino como objeto). */
export const describeSuggested = (finding: ContractReviewFinding): ChangeLine[] => {
  const next = parseValueObject(finding.suggestedValue);
  if (!next) return [];
  return changeLines(next, parseValueObject(finding.currentValue) ?? finding.currentValues ?? {});
};

export type ReviewWarningKind = 'unknownEnum' | 'brokenReferences' | 'droppedFindings' | 'truncated' | 'other';

export interface ReviewWarningInfo {
  kind: ReviewWarningKind;
  /** Texto original del servicio, para quien quiera el detalle. */
  raw: string;
  /** Datos extraídos del texto, para armar un título corto en el idioma del usuario. */
  params: Record<string, string | number>;
}

/**
 * Reconoce los avisos que manda el servicio de IA (RunContractReviewCommand, FindingAssembler, ContractsEnumNames)
 * por su forma, en español o inglés, para mostrarlos con un título y una explicación en lugar del texto crudo.
 */
export const describeReviewWarning = (raw: string): ReviewWarningInfo => {
  const text = raw.trim();
  let match = /^(?<field>[\w.]+): el valor (?<value>-?\d+) no existe en (?<enum>\w+)/i.exec(text) ?? /^(?<field>[\w.]+): value (?<value>-?\d+) does not exist in (?<enum>\w+)/i.exec(text);
  if (match?.groups) return { kind: 'unknownEnum', raw, params: { field: match.groups.field, value: match.groups.value, enum: match.groups.enum } };
  match = /(?<count>\d+) referencia\(s\) rota/i.exec(text) ?? /(?<count>\d+) broken reference/i.exec(text);
  if (match?.groups) return { kind: 'brokenReferences', raw, params: { count: Number(match.groups.count) } };
  match = /Se descartaron (?<count>\d+) hallazgo/i.exec(text) ?? /(?<count>\d+) finding\(s\) were discarded/i.exec(text);
  if (match?.groups) return { kind: 'droppedFindings', raw, params: { count: Number(match.groups.count) } };
  match = /Hay (?<total>\d+) hallazgos; se muestran los (?<shown>\d+)/i.exec(text) ?? /There are (?<total>\d+) findings; the (?<shown>\d+)/i.exec(text);
  if (match?.groups) return { kind: 'truncated', raw, params: { total: Number(match.groups.total), shown: Number(match.groups.shown) } };
  return { kind: 'other', raw, params: {} };
};
